import { create } from "zustand";
import dayjs from "dayjs";

import { IGallery, ILatestYearVisits, TService } from "~/interfaces";
import { IMapTiles, TMapset } from "~/map-interfaces";
import { getJSONItem, setItem } from "~/utils/localstorage";
import { getVisitItem } from "~/utils/utils";
import { DAYJS_FORMAT } from "~/const-files/const";
import { getLatestGalleryVisits, mergeGalleryVisits, updateVisitDataByGallery } from "~/providers/events";
import { insertVisit } from "~/providers/php-api";

// nastaveni stranky (vcetne mapovych dlazdic)
interface ISavedSettings extends IMapTiles {
	messengerUser: string;
	audioVolume: number;
	localDebug: boolean;
	photoSize: boolean;
}

// posledni shlednuty nahrany soubor
export interface ISavedUploadedFile {
	file: string;
	date: string;
}

interface ISavedStoreData {
	version: number;
	service: TService;
	settings: ISavedSettings;
	// shlednute galerie - seo: pocet shlednutych polozek
	visitData: ILatestYearVisits;
	// posledni zalogovana navsteva
	visit: {
		date: string;
		permissions: number;
	};
	messengerLastId: number;
	uploadedFile: ISavedUploadedFile;
}

// puvodni ploche ulozeni (bez verze)
interface ISavedStoreDataV1 {
	visitData: ILatestYearVisits;
	messengerUser: string;
	messengerLastId: number;
	visitDate: string;
	visitPermissions: number;
	audioVolume: number;
	useRetina: boolean;
	localDebug: boolean;
	photoSize: boolean;
	uploadedFile: string;
}

interface ISavedStore {
	saved: ISavedStoreData;
	setFromLS: (savedData: ISavedStoreData) => void;
	updateGalleryVisitCount: (gallery: IGallery) => void;
	allGalleryVisited: () => void;
	logVisit: (permissions: number, isAdmin: boolean) => void;
	setMessengerUser: (messengerUser: string) => void;
	setMessengerLastId: (messengerLastId: number) => void;
	setAudioVolume: (audioVolume: number) => void;
	setMapset: (mapset: TMapset) => void;
	setRetina: (useRetina: boolean) => void;
	setPanorama: (panorama: boolean) => void;
	setLocalDebug: (localDebug: boolean) => void;
	setPhotoSize: (photoSize: boolean) => void;
	setUploadedFile: (uploadedFile: ISavedUploadedFile) => void;
}

// klic je pro kazdou sluzbu zvlast - savedStoreDataMakuderovi, savedStoreDataRoman, savedStoreDataLenka
const LS_KEY = "savedStoreData";
const LS_VERSION = 2;
const DEBUG = false;

function getDefData(service: TService): ISavedStoreData {
	return {
		version: LS_VERSION,
		service,
		settings: {
			messengerUser: "Roman",
			audioVolume: 100,
			mapset: "outdoor",
			useRetina: false,
			panorama: false,
			localDebug: false,
			photoSize: false,
		},
		visitData: {},
		visit: {
			date: "",
			permissions: 0,
		},
		messengerLastId: 0,
		uploadedFile: {
			file: "",
			date: "",
		},
	};
}

function getLsName(service: TService) {
	return `${LS_KEY}${service[0].toUpperCase()}${service.slice(1)}`;
}

function migrateV1(service: TService, data: Partial<ISavedStoreDataV1>): ISavedStoreData {
	const defData = getDefData(service);

	return {
		...defData,
		settings: {
			messengerUser: data.messengerUser ?? defData.settings.messengerUser,
			audioVolume: data.audioVolume ?? defData.settings.audioVolume,
			mapset: defData.settings.mapset,
			useRetina: data.useRetina ?? defData.settings.useRetina,
			panorama: defData.settings.panorama,
			localDebug: data.localDebug ?? defData.settings.localDebug,
			photoSize: data.photoSize ?? defData.settings.photoSize,
		},
		visitData: data.visitData ?? {},
		visit: {
			date: data.visitDate ?? "",
			permissions: data.visitPermissions ?? 0,
		},
		messengerLastId: data.messengerLastId ?? 0,
		uploadedFile: {
			file: data.uploadedFile ?? "",
			date: "",
		},
	};
}

// null = v local storage nic neni (nebo neni dostupna)
function loadFromLs(service: TService): ISavedStoreData | null {
	const data = getJSONItem<Partial<ISavedStoreData>>(getLsName(service));

	if (!data || typeof data !== "object") {
		return null;
	}

	if (data.version !== LS_VERSION) {
		return migrateV1(service, data as unknown as Partial<ISavedStoreDataV1>);
	}

	const defData = getDefData(service);

	return {
		...defData,
		...data,
		version: LS_VERSION,
		service,
		settings: {
			...defData.settings,
			...data.settings,
		},
		visit: {
			...defData.visit,
			...data.visit,
		},
		uploadedFile: {
			...defData.uploadedFile,
			...data.uploadedFile,
		},
	};
}

/**
 * Zmena se vzdy aplikuje na aktualni data z local storage, ne na stav storu.
 * Pri vice otevrenych zalozkach by jinak jedna prepsala zmeny druhe (napr. shlednute galerie, nahrane soubory).
 */
function updateSaved(saved: ISavedStoreData, update: (data: ISavedStoreData) => ISavedStoreData) {
	const newSaved = update(loadFromLs(saved.service) ?? saved);

	setItem(getLsName(newSaved.service), JSON.stringify(newSaved));

	return {
		saved: newSaved,
	};
}

function updateSettings(saved: ISavedStoreData, settings: Partial<ISavedSettings>) {
	return updateSaved(saved, data => ({
		...data,
		settings: {
			...data.settings,
			...settings,
		},
	}));
}

async function sendVisitToBE(isAdmin: boolean) {
	const data = getVisitItem(isAdmin);

	await insertVisit(data);
}

export const savedStore = create<ISavedStore>(set => ({
	saved: getDefData("makuderovi"),
	setFromLS: savedData => set(() => ({
		saved: savedData,
	})),
	updateGalleryVisitCount: gallery => set(state => updateSaved(state.saved, data => ({
		...data,
		visitData: updateVisitDataByGallery(data.visitData, gallery),
	}))),
	allGalleryVisited: () => set(state => updateSaved(state.saved, data => ({
		...data,
		visitData: mergeGalleryVisits(data.visitData, getLatestGalleryVisits()),
	}))),
	logVisit: (permissions, isAdmin) => set(state => {
		if (!DEBUG && state.saved.settings.localDebug) {
			/* eslint-disable-next-line */
			console.log(`Skip log visit for localhost`);

			return state;
		}

		const curDate = dayjs().format(DAYJS_FORMAT);
		const lastVisit = (loadFromLs(state.saved.service) ?? state.saved).visit;

		if (!DEBUG && lastVisit.permissions === permissions && lastVisit.date === curDate) {
			return state;
		}

		sendVisitToBE(isAdmin);

		return updateSaved(state.saved, data => ({
			...data,
			visit: {
				date: curDate,
				permissions,
			},
		}));
	}),
	setMessengerUser: messengerUser => set(state => updateSettings(state.saved, { messengerUser })),
	setMessengerLastId: messengerLastId => set(state => updateSaved(state.saved, data => ({
		...data,
		messengerLastId,
	}))),
	setAudioVolume: audioVolume => set(state => updateSettings(state.saved, { audioVolume })),
	setMapset: mapset => set(state => updateSettings(state.saved, { mapset })),
	setRetina: useRetina => set(state => updateSettings(state.saved, { useRetina })),
	setPanorama: panorama => set(state => updateSettings(state.saved, { panorama })),
	setLocalDebug: localDebug => set(state => updateSettings(state.saved, { localDebug })),
	setPhotoSize: photoSize => set(state => updateSettings(state.saved, { photoSize })),
	setUploadedFile: uploadedFile => set(state => updateSaved(state.saved, data => ({
		...data,
		uploadedFile,
	}))),
}));

export function savedStoreInit(service: TService = "makuderovi") {
	const lsName = getLsName(service);

	savedStore.getState().setFromLS(loadFromLs(service) ?? getDefData(service));

	// zmeny z jinych zalozek stejne sluzby
	window.addEventListener("storage", event => {
		if (event.key === lsName || event.key === null) {
			savedStore.getState().setFromLS(loadFromLs(service) ?? getDefData(service));
		}
	});
}
